import React from "react";
import {
  AVATAR_SIZE_PX,
  Box,
  cn,
  EntityAvatar,
  EntityAvatarFallback,
  EntityAvatarImage,
  EntityIconTile,
  getInitials,
  Icon,
  resolveEntityColor,
  Text,
  type Color,
  type EntityAvatarSize,
  type IconName,
} from "@salestrak/ui";

import { resolveEntityVisual, type EntityData } from "#app/lib/entity-visuals";
import { presenceDotProps, usePresence } from "#app/lib/data/presence-provider";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import { StageIcon } from "./stage-icon";

import blackLogo from "#app/assets/images/black-logo.png";
import type { Omittable } from "@salestrak/ui";

export type EntityVisualSize = "2xs" | "xs" | "sm" | "md" | "lg" | "xl";

// Default size "sm" maps avatars one Avatar size down ("xs") so photo/initials
// circles sit optically level with the lighter icon glyphs at the same box.
const AVATAR_SIZE_MAP: Record<EntityVisualSize, EntityAvatarSize> = {
  "2xs": "2xs",
  xs: "xs",
  sm: "xs",
  md: "default",
  lg: "lg",
  xl: "xl",
};

const AVATAR_TEXT_SIZE_MAP: Record<EntityVisualSize, "2xs" | "xs" | "sm"> = {
  "2xs": "2xs",
  xs: "2xs",
  sm: "2xs",
  md: "sm",
  lg: "sm",
  xl: "sm",
};

// Declared as StageIcon's size union (a subset of IconSize) so one map serves
// both Icon and the stage pie glyph — a stage reads at the same visual weight
// as an icon kind.
const ICON_SIZE_MAP: Record<EntityVisualSize, "xs" | "sm" | "md" | "lg" | "xl"> = {
  "2xs": "xs",
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl",
};

// Shared footprint (px) so avatars, icons, and stage glyphs all occupy the same
// box at a given size and align in rows/columns. Derived from AVATAR_SIZE_PX via
// the same semantic→Avatar-size remap so it can't drift from the avatar circle.
const FOOTPRINT_PX_MAP: Record<EntityVisualSize, number> = {
  "2xs": AVATAR_SIZE_PX[AVATAR_SIZE_MAP["2xs"]],
  xs: AVATAR_SIZE_PX[AVATAR_SIZE_MAP.xs],
  sm: AVATAR_SIZE_PX[AVATAR_SIZE_MAP.sm],
  md: AVATAR_SIZE_PX[AVATAR_SIZE_MAP.md],
  lg: AVATAR_SIZE_PX[AVATAR_SIZE_MAP.lg],
  xl: AVATAR_SIZE_PX[AVATAR_SIZE_MAP.xl],
};

// Logo glyph size class per avatar size — the logo sits inside the circle the
// way an initial would (roughly single-character height), not edge-to-edge.
const SYSTEM_LOGO_CLASS: Record<EntityAvatarSize, string> = {
  "2xs": "size-2.5",
  xs: "size-3",
  sm: "size-3.5",
  default: "size-4",
  lg: "size-5",
  xl: "size-6",
};

// The SalesTrak author sentinel: the brand logo as a centered glyph in the
// primary-colored fallback circle. Domain-owned — the library avatar knows
// nothing about the brand. Black logo + dark:invert covers both themes.
function SystemEntityAvatar({
  size,
  muted,
  invited,
}: Omittable<{
  size: EntityAvatarSize;
  muted?: boolean;
  invited?: boolean;
}>) {
  const gray = muted || invited;
  return (
    <EntityAvatar
      size={size}
      muted={muted}
      invited={invited}
      alt="SalesTrak"
      className={cn(
        "items-center justify-center",
        gray ? "bg-muted" : "bg-primary",
        invited && "opacity-40",
      )}
    >
      <EntityAvatarImage
        src={blackLogo}
        alt="SalesTrak"
        className={cn(
          "object-contain dark:invert",
          SYSTEM_LOGO_CLASS[size],
          gray && "grayscale",
        )}
      />
    </EntityAvatar>
  );
}

export type EntityVisualProps = Omittable<{
  entity: EntityData;
  size?: EntityVisualSize;
  padded?: boolean;
  system?: boolean;
}>;

function EntityVisualComponent({
  entity,
  size = "sm",
  padded,
  system,
}: EntityVisualProps) {
  const visual = resolveEntityVisual(entity);

  // Live presence dot for member avatars. Resolved unconditionally (hooks
  // can't be conditional) — returns undefined for non-user kinds, unknown
  // members, or while the first presence poll is in flight; Avatar itself
  // suppresses the dot for suspended/invited avatars where "online" is
  // meaningless.
  const presence = usePresence(
    entity.kind === "user" ? entity.id : undefined,
  );
  // Profile images live in the PHOTOS-flagged media store. With the flag off
  // the avatar shows its initials fallback rather than fetching the image.
  // `hasFlag` answers false until flags resolve, so the image is kept while
  // they are in flight — gating on it there would flash initials on every
  // avatar in the app on each load and each workspace switch.
  const { ready, hasFlag } = useFeatureFlags();
  const photosEnabled = !ready || hasFlag(FeatureFlagKey.PHOTOS);

  if (visual.type === "avatar") {
    const avatarSize = AVATAR_SIZE_MAP[size];
    const isMuted = visual.suspended;
    const isInvited = visual.invited;
    const displayName = system ? "SalesTrak" : visual.label;

    if (system) {
      return (
        <SystemEntityAvatar size={avatarSize} muted={isMuted} invited={isInvited} />
      );
    }

    return (
      <EntityAvatar
        size={avatarSize}
        muted={isMuted}
        invited={isInvited}
        alt={displayName}
        presence={presence ? presenceDotProps(presence) : undefined}
      >
        {photosEnabled && visual.user?.profileImage ? (
          <EntityAvatarImage src={visual.user.profileImage} alt={displayName} />
        ) : null}
        <EntityAvatarFallback>
          <Text size={AVATAR_TEXT_SIZE_MAP[size]} weight="medium" leading="none">
            {getInitials(displayName)}
          </Text>
        </EntityAvatarFallback>
      </EntityAvatar>
    );
  }

  // Icon kinds with no glyph (e.g. sources) render label-only — no empty box.
  if (visual.type === "icon" && visual.icon == null) return null;

  const box = FOOTPRINT_PX_MAP[size];

  const resolvedPadded =
    padded ??
    (entity.kind === "team" ||
      entity.kind === "workflow" ||
      entity.kind === "issueType" ||
      entity.kind === "issueSource");

  // Explicit `padded={false}` = a bare inline glyph (e.g. a chip sitting
  // mid-sentence): no tile AND no footprint box, so spacing hugs the glyph.
  // The footprint box exists to align mixed visuals in lists — an opt-out of
  // the tile is also an opt-out of that alignment contract. Applies uniformly
  // to stage and icon kinds so an inline chip can't drift by visual type.
  if (padded === false) {
    if (visual.type === "stage") {
      return <StageIcon stage={visual.stage} size={ICON_SIZE_MAP[size]} />;
    }
    return (
      <Icon
        name={visual.icon as IconName}
        size={ICON_SIZE_MAP[size]}
        style={visual.color ? { color: resolveEntityColor(visual.color) } : undefined}
      />
    );
  }

  // Render the pie at the icon-glyph size (not the full footprint) so a stage
  // reads at the same visual weight as icon kinds; the footprint box still sets
  // the shared alignment box. Filling the box made stage pies ~2× every other
  // glyph and overflow compact controls.
  if (visual.type === "stage") {
    return (
      <Box
        width={box}
        height={box}
        direction="row"
        align="center"
        justify="center"
        shrink={false}
      >
        <StageIcon stage={visual.stage} size={ICON_SIZE_MAP[size]} padded={padded} />
      </Box>
    );
  }

  // Padded icon kinds render the canonical tinted tile at the full footprint,
  // matching EntityGroup's icon slots; unpadded ones center the bare glyph in
  // the footprint box.
  if (resolvedPadded) {
    return (
      <EntityIconTile
        icon={visual.icon as IconName}
        color={visual.color}
        size={ICON_SIZE_MAP[size]}
        boxSize={box}
      />
    );
  }

  return (
    <Box
      width={box}
      height={box}
      direction="row"
      align="center"
      justify="center"
      shrink={false}
    >
      <Icon
        name={visual.icon as IconName}
        size={ICON_SIZE_MAP[size]}
        style={visual.color ? { color: resolveEntityColor(visual.color) } : undefined}
      />
    </Box>
  );
}

EntityVisualComponent.displayName = "EntityVisual";

export const EntityVisual = React.memo(EntityVisualComponent);

// A neutral, non-entity glyph (e.g. an "All projects" rollup) rendered inside the
// EXACT same footprint box as an icon-kind entity visual, so it lines up with
// EntityTag chips in the same list. Unlike a real entity it is NOT padded by
// default — no tinted background square — since it isn't an entity icon; the
// footprint box alone keeps it aligned. Opt into `padded` only if a caller wants
// the chip-style tile. Use this instead of a bare <Icon> among entity chips.
export function NeutralEntityVisual({
  icon,
  color = "muted",
  size = "sm",
  padded = false,
}: Omittable<{
  icon: IconName;
  color?: Color;
  size?: EntityVisualSize;
  padded?: boolean;
}>) {
  const box = FOOTPRINT_PX_MAP[size];
  if (padded) {
    return (
      <EntityIconTile icon={icon} color={color} size={ICON_SIZE_MAP[size]} boxSize={box} />
    );
  }
  return (
    <Box
      width={box}
      height={box}
      direction="row"
      align="center"
      justify="center"
      shrink={false}
    >
      <Icon name={icon} size={ICON_SIZE_MAP[size]} color={color} />
    </Box>
  );
}
